/**
 * Task: PDFDownloadCard — one card per PDF in the documents catalog.
 */
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, FileText, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PDFDownloadCard({ file }) {
  const downloadUrl = `${BACKEND_URL}${file.download_url}`;
  // Public URL is faster (no auth, served by frontend static) — prefer for preview
  const previewUrl = file.public_url;

  return (
    <Card
      className="p-5 hover:shadow-md transition-shadow border-2 border-slate-200 hover:border-indigo-300"
      data-testid={`pdf-card-${file.name}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-rose-700 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
          <FileText className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-semibold text-slate-900 leading-tight">{file.title}</h3>
            <span className="text-xs text-slate-400 whitespace-nowrap">{file.size_human}</span>
          </div>
          <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{file.description}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-bold bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">
              {file.category}
            </span>
            <span className="text-[10px] text-slate-500">For: {file.audience}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
        <Button
          asChild
          variant="default"
          size="sm"
          className="flex-1"
          data-testid={`pdf-download-${file.name}`}
        >
          <a href={downloadUrl} download={file.name}>
            <Download className="h-4 w-4 mr-1.5" />
            Download
          </a>
        </Button>
        <Button
          asChild
          variant="outline"
          size="sm"
          data-testid={`pdf-preview-${file.name}`}
        >
          <a href={previewUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4 mr-1.5" />
            Preview
          </a>
        </Button>
      </div>
    </Card>
  );
}
